import { useEffect, useState } from 'react';

import {
  type IPopupSession,
  isOpenedByPod,
  redeemPopupTicket,
  takeStrippedPopupTicket,
} from 'components/CallScribe/popupTicket';
import { isSoftphonePopupView } from 'components/CallScribe/popout';
import { calleeLabel, readPopupCallContext } from 'components/CallScribe/popupCallContext';
import { acquireLiveCallLock, releaseLiveCallLock } from 'components/CallScribe/liveCallLease';
import { adoptSharedSession } from 'utils/popupSession';

export interface IPopupLaunch {
  signingIn: boolean;
  launchedByPod: boolean;
  signedInAs?: string;
}

const NOT_LAUNCHED: IPopupLaunch = { signingIn: false, launchedByPod: false };
const NO_FALLBACK = { crmSourceId: null, externalTicketId: null };

const claimLiveCall = (): Promise<boolean> => {
  if (!isSoftphonePopupView()) return Promise.resolve(false);
  const context = readPopupCallContext(window.location.search, NO_FALLBACK);
  if (!context.toNumber) return Promise.resolve(false);
  return acquireLiveCallLock({ callId: null, callee: calleeLabel(context), toNumber: context.toNumber });
};

export const usePopupLaunch = (
  enabled: boolean,
  onSignedIn: (session: IPopupSession) => void,
): IPopupLaunch => {
  const [ticket] = useState<string | null>(() => {
    if (enabled) adoptSharedSession();
    const taken = takeStrippedPopupTicket();
    return enabled && taken !== null && isOpenedByPod() ? taken : null;
  });
  const [launch, setLaunch] = useState<IPopupLaunch>(
    ticket === null ? NOT_LAUNCHED : { signingIn: true, launchedByPod: false },
  );

  useEffect(() => {
    if (ticket === null) return undefined;
    let cancelled = false;
    const claimed = claimLiveCall();
    void redeemPopupTicket(ticket).then(session => {
      if (cancelled) return;
      if (session === null) {
        void claimed.then(ok => { if (ok) releaseLiveCallLock(); });
        setLaunch(NOT_LAUNCHED);
        return;
      }
      onSignedIn(session);
      setLaunch({ signingIn: false, launchedByPod: true, signedInAs: session.username });
    });
    return () => { cancelled = true; };
  }, [ticket]);

  return launch;
};
